import { initializeApp } from 'firebase/app';
import { firebaseConfig } from './firebase.config';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  memoryLocalCache,
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Initialize Firebase
const FirebaseApp = initializeApp(firebaseConfig);

// Initialize services
const auth = getAuth(FirebaseApp);
// Safari Private Browsing (and iOS with "Block All Cookies") throws a
// SecurityError synchronously when indexedDB.open() is called. Probe first
// so we can fall back to in-memory cache instead of crashing every Firestore
// call for those users.
const _canUseIndexedDB = (() => {
  try {
    indexedDB.open('__gs_probe__');
    return true;
  } catch (_) {
    return false;
  }
})();

// `persistentMultipleTabManager` coordinates several desktop tabs via
// IndexedDB + localStorage. Safari (especially iOS) has a long history of
// IndexedDB bugs that surface as Firestore reads that never resolve — e.g. a
// shared `/song/:id` page stuck on "Loading…". Single-tab persistence keeps
// offline cache without cross-tab lease traffic; most sessions are one tab.
const db = initializeFirestore(FirebaseApp, {
  localCache: _canUseIndexedDB
    ? persistentLocalCache({ tabManager: persistentSingleTabManager() })
    : memoryLocalCache(),
});
const functions = getFunctions(FirebaseApp, 'us-central1');

// Local dev: point callable / HTTP functions at the Firebase emulator when
// `REACT_APP_USE_FUNCTIONS_EMULATOR=true`. Port stays in sync with
// `firebase.json → emulators.functions.port` (currently 7001). This flag is
// read at bundle time by CRA so prod builds never try to connect locally.
if (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true') {
  const host = process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST || 'localhost';
  const port = Number(
    process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || 7001
  );
  // eslint-disable-next-line no-console
  console.info(
    `[firebase] Using Functions emulator at http://${host}:${port}`
  );
  connectFunctionsEmulator(functions, host, port);

  if (
    process.env.NODE_ENV === 'development' &&
    (process.env.REACT_APP_SEARCH_ENGINE || '').toLowerCase() === 'meili'
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      '[firebase] Meili search: the Functions emulator talks to Meilisearch ' +
        'via functions/.env (MEILI_HOST + MEILI_API_KEY). If those are unset, ' +
        'search defaults to http://localhost:7700 — not your hosted index — so ' +
        'curl against Railway can work while localhost search does not. Fix: ' +
        'add MEILI_HOST and MEILI_API_KEY to functions/.env, or set ' +
        'REACT_APP_USE_FUNCTIONS_EMULATOR=false in .env.development.local to ' +
        'use production Cloud Functions for callables.'
    );
  }
}


const Providers = { google: new GoogleAuthProvider() };

export { FirebaseApp, auth, db, functions, Providers };
